import type { DcpCheckStatus, SageBankAccountRow, SageCheckRow } from '@/lib/db-tenant'
import type { RenderedSageSyncWarning } from '@dcp/shared/schemas/integrations'
import type { Facility } from '@/types/banking'

/**
 * Sage transaction-state codes that feed the Outstanding Checks view.
 *
 * Only Posted (`'C'`) checks are considered outstanding (DCP-244). DCP maps
 * Posted checks to status `Issued`; Paid and Voided statuses are user-only
 * overrides managed via the DCP inline editor and never come from Sage.
 *
 * The OutstandingChecksPage list filter and `aggregateChecksByEntity` below
 * MUST stay in lockstep with this constant — it is the single source of truth
 * that keeps the SageIntacctPage badge counter in agreement with the rows
 * shown on /banking/outstanding-checks. The SQL function
 * `sage_checks_aggregate_by_entity` must also match (see its WHERE clause).
 */
export const OUTSTANDING_CHECK_STATES = ['C'] as const
export type OutstandingCheckState = (typeof OUTSTANDING_CHECK_STATES)[number]

/**
 * Effective display/calculation status of a check (DCP-213).
 * An explicit DCP override (`dcp_check_status`) always wins; otherwise the status is
 * derived from Date Cashed — a cashed check reads as 'paid', an un-cashed one
 * as 'issued'. Nothing synced from Sage feeds the status: every imported check
 * is 'issued' until a user marks it paid or voided.
 *
 * Server-side twin: `checkStatusFilter` (`sage-check-status-filter.ts`) encodes
 * this same order as a PostgREST filter for the Status quick filter — change
 * both together.
 *
 * @param row - A sage_checks row (or compatible pick) carrying dcp_check_status and date_cashed.
 * @returns The canonical DcpCheckStatus for display and calculation logic.
 */
export function effectiveCheckStatus(
  row: Pick<SageCheckRow, 'dcp_check_status' | 'date_cashed'>,
): DcpCheckStatus {
  if (row.dcp_check_status) return row.dcp_check_status as DcpCheckStatus
  return row.date_cashed ? 'paid' : 'issued'
}

/**
 * Returns true when a check should count toward Outstanding Checks / Book Balance.
 * A check is outstanding only when its effective status is 'issued' AND it has
 * not been cashed.
 *
 * LOCKSTEP CONTRACT. The authoritative SQL predicate, shared by
 * `sage_checks_outstanding_total`, `sage_checks_aggregate_by_entity` and
 * `get_cash_position_summary`, is:
 *
 *   c.state = 'C' AND c.date_cashed IS NULL
 *   AND (c.dcp_check_status IS NULL OR c.dcp_check_status = 'issued')
 *
 * The `outstandingOnly` filter in `sageChecks.list`/`count` encodes the same
 * rule for PostgREST. This function is that predicate minus the `state` clause
 * (callers gate on `OUTSTANDING_CHECK_STATES` themselves). The `!date_cashed`
 * clause is load-bearing: without it an `'issued'` override on a row that also
 * carries a `date_cashed` would be counted here but excluded server-side,
 * diverging the SageIntacctPage badge from the list/tiles.
 *
 * @param row - A sage_checks row (or compatible pick) carrying dcp_check_status and date_cashed.
 * @returns Whether the check is currently outstanding.
 */
export function isOutstandingCheck(
  row: Pick<SageCheckRow, 'dcp_check_status' | 'date_cashed'>,
): boolean {
  return effectiveCheckStatus(row) === 'issued' && !row.date_cashed
}

/**
 * Single source of truth for the cross-field rule when assigning a DCP status
 * override to a check (DCP-275). The row-level inline editor saves Status and
 * Date Cashed together, so the only invariant left to enforce is:
 *   - 'paid'   requires a Date Cashed (a cashed check must record WHEN it
 *              cleared, otherwise the Outstanding/Book Balance tiles can't
 *              attribute it to a period),
 *   - 'issued'/'voided' are always allowed — Date Cashed belongs to 'paid' only,
 *              so the editor/data layer clear the date on save for any non-paid
 *              status; an empty date is therefore never a *precondition* the
 *              caller has to satisfy beforehand.
 *
 * Shared by the data layer (`sageChecks.updateCheck`, which surfaces `reason`
 * as its error message after resolving the final date) and the inline UI editor
 * (`DateCashedCell`, which gates the Save button on the same rule), so the rule
 * cannot drift between them.
 *
 * @param status     - The status the user wants to assign.
 * @param dateCashed - The check's resolved Date Cashed (ISO date) or null.
 */
export function canAssignStatus(
  status: DcpCheckStatus,
  dateCashed: string | null,
): { ok: true } | { ok: false; reason: string } {
  if (status === 'paid' && dateCashed === null) {
    return { ok: false, reason: 'Status "Paid" requires a Date Cashed.' }
  }
  return { ok: true }
}

/**
 * Resolve the Date Cashed that should actually be persisted for a given status
 * (DCP-275). Date Cashed belongs to 'paid' ONLY — a cashed check records WHEN it
 * cleared; any other status ('issued'/'voided') clears the date so a stale value
 * can't survive and re-derive the row as 'paid' via `effectiveCheckStatus`.
 *
 * Single source of truth shared by the data layer (`sageChecks.updateCheck`), the
 * optimistic cache patch (`useUpdateSageCheck`), and the UI Save gate
 * (`OutstandingChecksPage`) so the "paid-only date" rule cannot drift between
 * them — it always pairs with `canAssignStatus`, which is validated against this
 * resolved date.
 *
 * @param status     - The status being assigned.
 * @param dateCashed - The Date Cashed the caller wants to set (ISO date) or null.
 * @returns The date to persist: the caller value for 'paid', otherwise null.
 */
export function resolveDateCashed(
  status: DcpCheckStatus,
  dateCashed: string | null,
): string | null {
  return status === 'paid' ? dateCashed : null
}

/**
 * Per-row sync status for a facility, derived client-side from the latest
 * sync_state + sage_bank_accounts data. Drives the Status badge.
 */
export type FacilityStatus = 'synced' | 'unmatched' | 'pending' | 'not_configured'

/** Aggregated check counts and outstanding amount for one Sage bank account. */
export interface CheckAggregate {
  total: number
  outstanding: number
  outstandingAmount: number
}

/**
 * Compute the per-facility sync status from the three inputs that determine it.
 * The function is pure so it's easy to unit-test if we ever want to.
 *
 * @param sageLocationId - The facility's configured Sage location ID, or null if not set.
 * @param count - Number of Sage bank accounts matched to this location.
 * @param syncRanAtLeastOnce - Whether the bank-accounts sync has ever completed.
 * @returns One of four status strings driving the Status badge.
 *
 * @example
 * // Facility was never linked to Sage — admin still needs to fill the
 * // sage_location_id field on /settings/facilities.
 * deriveFacilityStatus({ sageLocationId: null, count: 0, syncRanAtLeastOnce: true })
 * // → 'not_configured'
 *
 * @example
 * // Configured + sync surfaced at least one matching bank account.
 * deriveFacilityStatus({ sageLocationId: 'PH_BF934', count: 3, syncRanAtLeastOnce: true })
 * // → 'synced'
 *
 * @example
 * // Configured but the bank-accounts sync hasn't run yet (e.g. brand-new
 * // integration). Show "Pending" instead of "Unmatched" so the user
 * // doesn't think the LOCATIONID is wrong before any data has flowed.
 * deriveFacilityStatus({ sageLocationId: 'PH_BF934', count: 0, syncRanAtLeastOnce: false })
 * // → 'pending'
 *
 * @example
 * // Configured AND sync has run, but Sage returned zero bank accounts for
 * // this LOCATIONID — typo in the facility row or the location really is
 * // empty in Sage. Surfaces as a red badge so the admin investigates.
 * deriveFacilityStatus({ sageLocationId: 'TYPO_42', count: 0, syncRanAtLeastOnce: true })
 * // → 'unmatched'
 */
export function deriveFacilityStatus({
  sageLocationId,
  count,
  syncRanAtLeastOnce,
}: {
  sageLocationId: string | null
  count: number
  syncRanAtLeastOnce: boolean
}): FacilityStatus {
  if (!sageLocationId) return 'not_configured'
  if (count > 0) return 'synced'
  // Has a sage_location_id but no matched bank accounts.
  if (!syncRanAtLeastOnce) return 'pending'
  return 'unmatched'
}

/**
 * Aggregate sage_checks rows by `financial_entity` so the Checks table can
 * render per-bank-account counts.
 *
 * Outstanding semantics: a check is outstanding when (a) its state is one
 * of `OUTSTANDING_CHECK_STATES` (Posted only, per DCP-244) AND (b) its effective
 * status (as determined by `isOutstandingCheck`) is 'issued'. The effective
 * status respects the DCP override column: a check with `dcp_check_status: 'voided'`
 * or `dcp_check_status: 'paid'` is excluded even when its Sage state is still 'C'
 * and `date_cashed` is null. Routing every decision through `isOutstandingCheck`
 * (rather than re-deriving the rule here) is what keeps the SageIntacctPage badge
 * counter in lockstep with the list shown on /banking/outstanding-checks and with
 * the server-side SQL predicate quoted there.
 *
 * Bank-clearance tracking (Plaid debit cross-checking) is deferred to a
 * follow-up — once it lands, "outstanding" can become a derived signal
 * instead of an explicit override. Until then `isOutstandingCheck` is the
 * single authoritative definition of effective-issued status.
 *
 * @param checks - Array of sage_checks rows from the tenant database.
 * @returns Map keyed by financial_entity ID containing total, outstanding count, and outstanding amount.
 */
export function aggregateChecksByEntity(
  checks: SageCheckRow[],
): Map<string, CheckAggregate> {
  const out = new Map<string, CheckAggregate>()
  const outstandingSet: Set<string> = new Set(OUTSTANDING_CHECK_STATES)
  for (const c of checks) {
    if (!c.financial_entity) continue
    const entry = out.get(c.financial_entity) ?? {
      total: 0,
      outstanding: 0,
      outstandingAmount: 0,
    }
    entry.total += 1
    // Gate on both the Sage state allowlist AND the effective status:
    // `isOutstandingCheck` encapsulates the dcp_check_status override logic
    // (voided/paid overrides always win) and the date_cashed fallback.
    // Without the effective-status check the badge would over-count rows
    // the user has already voided via the DCP inline editor.
    if (c.state && outstandingSet.has(c.state) && isOutstandingCheck(c)) {
      entry.outstanding += 1
      // total_paid is the disbursed amount for Posted ('C') checks and is
      // the correct field to accumulate into the outstanding dollar total.
      // Generated types say total_paid is non-null
      // `number`, but we gate the null/undefined path because the
      // Supabase row shape can lie (column added before backfill, schema
      // drift). `?? 0` inside Number() avoids the
      // `Number(undefined) → NaN` trap that would propagate through the
      // running sum.
      //
      // The `Number.isFinite` filter guards against a second NaN-spread
      // path: if schema drift hands us a non-numeric value (`"abc"` from
      // a stringly-typed column, `null` after a future migration), the
      // outer `Number()` returns `NaN`, and a single `NaN +=` poisons
      // every subsequent sum for the entity — the SageIntacctPage badge
      // would render literal "NaN". Drop non-finite contributions on the
      // floor; the row is still counted via the `outstanding += 1` above
      // so the count stays honest.
      const amount = Number(c.total_paid ?? 0)
      if (Number.isFinite(amount)) {
        entry.outstandingAmount += amount
      } else if (import.meta.env.DEV) {
        // Operator-visible breadcrumb when an upstream column lies — the
        // outstanding count still increments, but the dollar total has
        // dropped this row. Without the warn, schema-drift bugs (e.g.
        // `total_paid` becoming a string column after a botched migration,
        // or a corrupt Sage payload landing as `'abc'`) would silently
        // under-report by an unknown amount.
        console.warn(
          `aggregateChecksByEntity: skipping non-finite total_paid for ` +
            `check record_no=${c.record_no}; raw value=${JSON.stringify(c.total_paid)}`,
        )
      }
    }
    out.set(c.financial_entity, entry)
  }
  return out
}

/**
 * Render a RenderedSageSyncWarning as a single human-readable line. For
 * `unmatched_location` we synthesize a sentence so the table doesn't have to
 * carry a `message`; other kinds use their `message` directly.
 *
 * @param warning - A validated warning from parseSageSyncWarnings().
 * @returns Human-readable string for display in the coverage warnings list.
 */
export function formatWarning(warning: RenderedSageSyncWarning): string {
  if (warning.kind === 'unmatched_location') {
    return `${warning.facility_name}: no bank accounts found for LOCATIONID ${warning.sage_location_id}`
  }
  return warning.message
}

/** What the Sage bank-account catalog says about one facility. */
export interface FacilityBankAccountCoverage {
  count: number
  /** The latest `last_seen_at` among the facility's accounts; null when none has one. */
  lastSeenAt: string | null
}

/**
 * Per facility: how many Sage bank accounts it owns and when one was last seen
 * in Sage. Each account goes to the facility that WINS its location key
 * ({@link resolveFacilityWinnersByLocationKey}), so a losing case-variant
 * facility gets nothing — the same attribution as the Bank Accounts table and
 * Cash Position. Client-side because the catalog is small and already loaded.
 *
 * @param bankAccounts - sage_bank_accounts rows
 * @param facilities - every facility, inactive ones included, so the winner
 *        rule sees the same candidates as everywhere else
 * @returns facility id → coverage; facilities owning no account are absent
 */
export function bankAccountCoverageByFacility(
  bankAccounts: readonly SageBankAccountRow[],
  facilities: readonly Pick<Facility, 'id' | 'name' | 'sage_location_id' | 'is_active'>[],
): Map<string, FacilityBankAccountCoverage> {
  const winners = resolveFacilityWinnersByLocationKey(facilities)
  const coverage = new Map<string, FacilityBankAccountCoverage>()
  for (const ba of bankAccounts) {
    const winner = winners.get(ba.location_id.toUpperCase())
    if (!winner) continue
    const current = coverage.get(winner.id) ?? { count: 0, lastSeenAt: null }
    coverage.set(winner.id, {
      count: current.count + 1,
      lastSeenAt:
        ba.last_seen_at && (!current.lastSeenAt || ba.last_seen_at > current.lastSeenAt)
          ? ba.last_seen_at
          : current.lastSeenAt,
    })
  }
  return coverage
}

/** The facility that owns a Sage location key — see {@link resolveFacilityWinnersByLocationKey}. */
export interface FacilityLocationWinner {
  id: string
  isActive: boolean
  name: string
}

/**
 * Resolve, per UPPER-cased Sage location key, WHICH facility owns it.
 *
 * `facilities.sage_location_id` is `TEXT UNIQUE` case-SENSITIVELY and its format
 * CHECK permits mixed case, so `LOC-1` and `loc-1` can be two different
 * facilities while Sage reports a single canonical casing. Every location↔facility
 * comparison in this product is therefore case-INSENSITIVE (DCP-262), and every
 * one of them resolves the resulting collision with the SAME rule: active first,
 * then lowest id — spelled out in the `sba_facility` CTE of
 * `get_cash_position_summary` (`ORDER BY sba.financial_entity_id, f.is_active
 * DESC, f.id`) and mirrored by `facilitySageLocationIndex` in `db-tenant.ts` and
 * by the sync tasks. Callers look an account up with
 * `map.get(account.location_id.toUpperCase())`.
 *
 * Facilities with no `sage_location_id` are skipped: they own no key.
 *
 * @param facilities - the full facility list, INCLUDING inactive ones, so a
 *        deactivated facility's name still resolves for display. Activity is
 *        carried on each winner rather than filtered out here.
 * @returns key → the winning facility's id, activity flag and display name.
 */
export function resolveFacilityWinnersByLocationKey(
  facilities: readonly Pick<Facility, 'id' | 'name' | 'sage_location_id' | 'is_active'>[],
): Map<string, FacilityLocationWinner> {
  const winnerByKey = new Map<string, FacilityLocationWinner>()
  for (const f of facilities) {
    if (!f.sage_location_id) continue
    const key = f.sage_location_id.toUpperCase()
    const isActive = f.is_active === true
    const current = winnerByKey.get(key)
    if (
      current === undefined ||
      (isActive && !current.isActive) ||
      (isActive === current.isActive && f.id < current.id)
    ) {
      winnerByKey.set(key, { id: f.id, isActive, name: f.name })
    }
  }
  return winnerByKey
}
